import React, { useState } from "react";
import Button from "../../form/Button";
import Modal from "../../elements/Modal";
import { classNames } from "../../../helpers/classNames";
import DatePicker from "../../form/DatePicker";
import { useQuery } from "@tanstack/react-query";
import { categoryList } from "../../../services/categoryService";
import { lngDetect } from "../../../helpers/lngDetect";
import { addTimeToDate } from "../../../helpers";
import { t } from "i18next";

function generateQuarterOptions() {
  const quarters = [
    { startMonth: 0, label: "Jan–Mar" },
    { startMonth: 3, label: "Apr–Jun" },
    { startMonth: 6, label: "Jul–Sep" },
    { startMonth: 9, label: "Oct–Dec" },
  ];

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Find the index of the current quarter
  const startQuarterIndex = quarters.findIndex(
    (q) => currentMonth >= q.startMonth && currentMonth < q.startMonth + 3
  );

  const options = [];

  for (let i = 0; i < 5; i++) {
    const quarter = quarters[(startQuarterIndex + i) % 4];
    const yearOffset = Math.floor((startQuarterIndex + i) / 4);
    const year = currentYear + yearOffset;

    const start = new Date(year, quarter.startMonth, 1);
    const end = new Date(year, quarter.startMonth + 3, 0); // last day of 3rd month

    const label = `${quarter.label} ${year}`;
    const value = `${start.toString()} to ${end.toString()}`;

    options.push({ label, value });
  }

  options.push({ label: "Personalizado", value: "custom" });

  return options;
}

const EventFiltersModal = ({
  isModalOpen = false,
  setIsModalOpen = () => {},
  onChange = () => {},
}) => {
  const optionDates = generateQuarterOptions();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState([]);
  const [selectedDance, setselectedDance] = useState([]);

  const { data: optionEventType } = useQuery({
    queryKey: ["event-type"],
    queryFn: async () => {
      const res = await categoryList({ type: "event-type" });
      return res?.docs;
    },
  });

  const { data: optionDanceStyle } = useQuery({
    queryKey: ["event-category"],
    queryFn: async () => {
      const res = await categoryList({ type: "event-category" });
      return res?.docs;
    },
  });

  const handleReset = () => {
    setSelectedDate(null);
    setSelectedEvent([]);
    setselectedDance([]);
  };

  return (
    <Modal
      isOpen={isModalOpen}
      setIsOpen={setIsModalOpen}
      title={t("Filter events")}
      size="xl5"
    >
      <div className="py-5 space-y-5">
        <div className="relative">
          <div className="text-2xl text-text-disabled font-bold capitalize mb-2">
            {t("dates")}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {optionDates.map((date) => (
              <div key={date?.value}>
                {date?.value !== "custom" ? (
                  <label className="cursor-pointer">
                    <input
                      type="radio"
                      name="quarter"
                      value={date?.value}
                      checked={selectedDate?.value === date?.value}
                      onChange={() =>
                        setSelectedDate({ type: "quarter", value: date?.value })
                      }
                      className="sr-only peer"
                    />
                    <div
                      className={classNames(`
                              h-10 px-5 flex items-center justify-center rounded-full text-sm font-semibold
                              transition-all border peer-checked:bg-primary peer-checked:text-white peer-checked:border-primary
                              bg-white text-gray-700 border-gray-300 hover:bg-gray-100
                            `)}
                    >
                      {date?.label}
                    </div>
                  </label>
                ) : (
                  <div
                    className={classNames(
                      "cursor-pointer h-10 px-5 flex items-center justify-center rounded-full text-sm font-semibold transition-all border border-gray-300 relative",
                      selectedDate?.type === "custom"
                        ? "border border-primary bg-primary text-white"
                        : ""
                    )}
                  >
                    <div
                      className={classNames(
                        "absolute top-0 left-0 w-full h-full flex items-center justify-center"
                      )}
                    >
                      {date?.label}
                    </div>
                    <div className="w-32 h-full">
                      <DatePicker
                        range
                        inputClassName="opacity-0"
                        position="relative"
                        datePickerClsss={"[&>div>button]:opacity-0"}
                        value={
                          selectedDate?.type === "custom"
                            ? selectedDate?.value
                            : { startDate: "", endDate: "" }
                        }
                        onChange={(val) => {
                          if (val?.startDate) {
                            setSelectedDate({
                              type: "custom",
                              value: `${val?.startDate.toString()} to ${val?.endDate.toString()}`,
                            });
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="text-2xl text-text-disabled font-bold capitalize mb-2">
            {t("Event type")}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {optionEventType?.data?.map((event) => (
              <label key={event?._id} className="cursor-pointer">
                <input
                  type="checkbox"
                  name="quarter"
                  value={event?._id}
                  checked={selectedEvent.includes(event?._id)}
                  onChange={() => {
                    setSelectedEvent(
                      selectedEvent.includes(event?._id)
                        ? selectedEvent.filter((e) => e !== event?._id)
                        : [...selectedEvent, event?._id]
                    );
                  }}
                  className="sr-only peer"
                />
                <div
                  className={`h-10 px-5 flex items-center justify-center rounded-full text-sm font-semibold
                              transition-all border
                              peer-checked:bg-primary peer-checked:text-white peer-checked:border-primary
                              bg-white text-gray-700 border-gray-300 hover:bg-gray-100`}
                >
                  {lngDetect(event?.name)}
                </div>
              </label>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="text-2xl text-text-disabled font-bold capitalize mb-2">
            {t("Dance style")}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {optionDanceStyle?.data?.map((dance) => (
              <label key={dance?._id} className="cursor-pointer">
                <input
                  type="checkbox"
                  name="quarter"
                  value={dance?._id}
                  checked={selectedDance.includes(dance?._id)}
                  onChange={() => {
                    setselectedDance(
                      selectedDance.includes(dance?._id)
                        ? selectedDance.filter((e) => e !== dance?._id)
                        : [...selectedDance, dance?._id]
                    );
                  }}
                  className="sr-only peer"
                />
                <div
                  className={`h-10 px-5 flex items-center justify-center rounded-full text-sm font-semibold
                              transition-all border
                              peer-checked:bg-primary peer-checked:text-white peer-checked:border-primary
                              bg-white text-gray-700 border-gray-300 hover:bg-gray-100`}
                >
                  {lngDetect(dance?.name)}
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="relative">
          <Button
            buttonLabel={t("clean filters")}
            buttonClasses={"px-0 bg-transparent"}
            buttonLabelClasses={"text-deep-purple capitalize text-lg"}
            buttonFunction={handleReset}
          />
        </div>
        <div className="flex items-center gap-10">
          {/* <div className="text-deep-purple text-lg capitalize font-bold">
            270 results
          </div> */}
          <div className="relative">
            <Button
              buttonLabel={t("Apply filters")}
              buttonLabelClasses={"capitalize text-lg"}
              buttonFunction={() => {
                const [startDate, endDate] =
                  selectedDate?.value?.split(" to ") || [];

                onChange({
                  date:
                    startDate && endDate
                      ? {
                          startDate: new Date(startDate),
                          endDate: new Date(endDate),
                        }
                      : undefined,
                  event: selectedEvent,
                  danceStyle: selectedDance,
                });
              }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EventFiltersModal;
